import {
  absoluteUrl,
  articlePath,
  listArticles,
  listIndexableArticles,
  listSiteFaqs,
  readingTimeMinutes,
} from "../content";
import {
  DEFAULT_LOCALE,
  LOCALES,
  LOCALE_TAGS,
  OG_LOCALES,
  localePath,
  messagesFor,
  type Locale,
} from "../i18n";
import {
  ARTICLES_PATH,
  FAQ_PATH,
  SITE_NAME,
  SITE_URL,
  type Article,
} from "../shared";
import type { HeadTag, PageHead } from "./head";
import {
  buildArticleJsonLd,
  buildBreadcrumbJsonLd,
  buildCollectionJsonLd,
  buildFaqPageJsonLd,
  buildOrganizationJsonLd,
  buildWebApplicationJsonLd,
  buildWebSiteJsonLd,
  type Crumb,
} from "./jsonLd";

/**
 * The head of every public page, and the list of pages that get one.
 *
 * The route list is derived from the article registry and the locale list
 * rather than written down, so adding an article or a language adds prerendered
 * documents, sitemap entries, and lines in `llms.txt` with no configuration
 * edit. A route that is not in this list is never emitted as static HTML, which
 * is the whole of the publication decision.
 *
 * Three paths exist in three languages: the homepage, the guide index, and the
 * questions page. The guides themselves are written in English and are not
 * translated, so they exist at one URL and declare no alternates. That asymmetry
 * is deliberate and it is the honest thing to tell a crawler: hreflang is a
 * claim that the same page exists in another language, and a claim pointing at
 * a page that is still in English is a claim Google drops the whole set over.
 */

/** The paths that exist in every language. */
export const LOCALIZED_PATHS = ["/", ARTICLES_PATH, FAQ_PATH] as const;

/**
 * The social card, drawn by `npm run seo:og` and committed under `public/`.
 *
 * One card serves every page and every language. Its dimensions are stated here
 * because a card whose declared size does not match the file is cropped by some
 * platforms and ignored by others, so the two are regenerated together.
 */
const SOCIAL_CARD = {
  path: "/og-card.png",
  type: "image/png",
  width: "1200",
  height: "630",
  alt: `${SITE_NAME}: two languages side by side on one printable sheet, split by a fold line`,
} as const;

/**
 * The alternates for a path that exists in every language.
 *
 * Every version points at every version including itself, because a set where
 * one page is missing from another page's list is a set Google discards. The
 * `x-default` goes to English, which is the version served to a reader whose
 * language the site does not speak.
 */
function alternateTags(path: string): HeadTag[] {
  const tags = LOCALES.map(
    (locale): HeadTag => ({
      tag: "link",
      attrs: {
        rel: "alternate",
        hreflang: LOCALE_TAGS[locale],
        href: absoluteUrl(localePath(locale, path)),
      },
    }),
  );

  tags.push({
    tag: "link",
    attrs: {
      rel: "alternate",
      hreflang: "x-default",
      href: absoluteUrl(localePath(DEFAULT_LOCALE, path)),
    },
  });

  return tags;
}

/** Every page shares the site-wide nodes, the social card, and the canonical. */
function baseTags(input: {
  path: string;
  locale: Locale;
  description: string;
  title: string;
  ogType: string;
  robots: string;
  /** False for the guides, which exist in English and nothing else. */
  translated: boolean;
}): HeadTag[] {
  const url = absoluteUrl(localePath(input.locale, input.path));

  return [
    { tag: "meta", attrs: { name: "description", content: input.description } },
    { tag: "meta", attrs: { name: "robots", content: input.robots } },
    { tag: "meta", attrs: { name: "googlebot", content: input.robots } },
    { tag: "link", attrs: { rel: "canonical", href: url } },
    ...(input.translated ? alternateTags(input.path) : []),
    { tag: "meta", attrs: { property: "og:type", content: input.ogType } },
    { tag: "meta", attrs: { property: "og:site_name", content: SITE_NAME } },
    { tag: "meta", attrs: { property: "og:locale", content: OG_LOCALES[input.locale] } },
    ...(input.translated
      ? LOCALES.filter((locale) => locale !== input.locale).map(
          (locale): HeadTag => ({
            tag: "meta",
            attrs: { property: "og:locale:alternate", content: OG_LOCALES[locale] },
          }),
        )
      : []),
    { tag: "meta", attrs: { property: "og:url", content: url } },
    { tag: "meta", attrs: { property: "og:title", content: input.title } },
    { tag: "meta", attrs: { property: "og:description", content: input.description } },
    { tag: "meta", attrs: { property: "og:image", content: absoluteUrl(SOCIAL_CARD.path) } },
    { tag: "meta", attrs: { property: "og:image:alt", content: SOCIAL_CARD.alt } },
    { tag: "meta", attrs: { property: "og:image:type", content: SOCIAL_CARD.type } },
    { tag: "meta", attrs: { property: "og:image:width", content: SOCIAL_CARD.width } },
    { tag: "meta", attrs: { property: "og:image:height", content: SOCIAL_CARD.height } },
    { tag: "meta", attrs: { name: "twitter:card", content: "summary_large_image" } },
    { tag: "meta", attrs: { name: "twitter:title", content: input.title } },
    { tag: "meta", attrs: { name: "twitter:description", content: input.description } },
    { tag: "meta", attrs: { name: "twitter:image", content: absoluteUrl(SOCIAL_CARD.path) } },
    { tag: "meta", attrs: { name: "twitter:image:alt", content: SOCIAL_CARD.alt } },
    { tag: "jsonLd", data: buildOrganizationJsonLd() },
    { tag: "jsonLd", data: buildWebSiteJsonLd() },
  ];
}

/**
 * The two directives, spelled out.
 *
 * `max-snippet:-1` and `max-image-preview:large` let a result or an AI overview
 * quote as much of a page as it wants to, which is the whole point of writing
 * passages that survive being quoted. `max-video-preview:-1` is here for
 * completeness rather than need: there is no video on the site, and a directive
 * set per page kind is one more thing to get wrong later.
 */
const INDEXABLE =
  "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1";
const HIDDEN = "noindex,nofollow";

export function buildHomeHead(locale: Locale = DEFAULT_LOCALE): PageHead {
  const t = messagesFor(locale);
  const url = absoluteUrl(localePath(locale, "/"));

  return {
    title: t.seo.homeTitle,
    lang: LOCALE_TAGS[locale],
    tags: [
      ...baseTags({
        path: "/",
        locale,
        title: t.seo.homeTitle,
        description: t.seo.homeDescription,
        ogType: "website",
        robots: INDEXABLE,
        translated: true,
      }),
      {
        tag: "jsonLd",
        data: buildWebApplicationJsonLd(url, locale, t.seo.homeDescription),
      },
    ],
  };
}

/** The trail rendered above a page and the trail a crawler is told about. */
export function articleIndexCrumbs(locale: Locale = DEFAULT_LOCALE): Crumb[] {
  const t = messagesFor(locale);

  return [
    { name: SITE_NAME, url: absoluteUrl(localePath(locale, "/")) },
    { name: t.nav.guides, url: absoluteUrl(localePath(locale, ARTICLES_PATH)) },
  ];
}

/** An article is English, so its trail is the English one whatever came before. */
export function articleCrumbs(article: Article): Crumb[] {
  return [
    ...articleIndexCrumbs(DEFAULT_LOCALE),
    { name: article.title, url: absoluteUrl(articlePath(article.slug)) },
  ];
}

export function faqCrumbs(locale: Locale = DEFAULT_LOCALE): Crumb[] {
  const t = messagesFor(locale);

  return [
    { name: SITE_NAME, url: absoluteUrl(localePath(locale, "/")) },
    { name: t.nav.questions, url: absoluteUrl(localePath(locale, FAQ_PATH)) },
  ];
}

export function buildArticleIndexHead(locale: Locale = DEFAULT_LOCALE): PageHead {
  const t = messagesFor(locale);
  const url = absoluteUrl(localePath(locale, ARTICLES_PATH));

  return {
    title: t.seo.articlesTitle,
    lang: LOCALE_TAGS[locale],
    tags: [
      ...baseTags({
        path: ARTICLES_PATH,
        locale,
        title: t.seo.articlesTitle,
        description: t.seo.articlesDescription,
        ogType: "website",
        robots: INDEXABLE,
        translated: true,
      }),
      { tag: "jsonLd", data: buildBreadcrumbJsonLd(articleIndexCrumbs(locale)) },
      {
        tag: "jsonLd",
        data: buildCollectionJsonLd({
          url,
          locale,
          name: t.seo.articlesLabel,
          description: t.seo.articlesDescription,
          entries: listIndexableArticles().map((article) => ({
            name: article.title,
            url: absoluteUrl(articlePath(article.slug)),
            description: article.description,
          })),
        }),
      },
    ],
  };
}

export function buildFaqHead(locale: Locale = DEFAULT_LOCALE): PageHead {
  const t = messagesFor(locale);
  const url = absoluteUrl(localePath(locale, FAQ_PATH));

  return {
    title: t.seo.faqTitle,
    lang: LOCALE_TAGS[locale],
    tags: [
      ...baseTags({
        path: FAQ_PATH,
        locale,
        title: t.seo.faqTitle,
        description: t.seo.faqDescription,
        ogType: "website",
        robots: INDEXABLE,
        translated: true,
      }),
      { tag: "jsonLd", data: buildBreadcrumbJsonLd(faqCrumbs(locale)) },
      {
        tag: "jsonLd",
        data: buildFaqPageJsonLd({
          url,
          locale,
          name: t.seo.faqLabel,
          description: t.seo.faqDescription,
          faqs: listSiteFaqs(locale),
        }),
      },
    ],
  };
}

/**
 * The head every page behind the app carries.
 *
 * A private route is never prerendered, so a static host answers it from the
 * SPA fallback with the homepage's indexable HTML until the bundle wakes up and
 * replaces the head. This directive is what closes that window, which is why it
 * ships a robots rule and deliberately nothing else: no canonical, no social
 * card, no structured data for a page nobody may index. It carries a `lang`
 * because the app follows the device language, and a document whose text is
 * Thai while its markup claims English is wrong for a screen reader too.
 */
export function buildPrivateHead(
  title: string,
  locale: Locale = DEFAULT_LOCALE,
): PageHead {
  return {
    title: `${title} | ${SITE_NAME}`,
    lang: LOCALE_TAGS[locale],
    tags: [
      { tag: "meta", attrs: { name: "robots", content: HIDDEN } },
      { tag: "meta", attrs: { name: "googlebot", content: HIDDEN } },
    ],
  };
}

export function articleTitle(article: Article): string {
  return `${article.title} | ${SITE_NAME}`;
}

export function buildArticleHead(article: Article): PageHead {
  const path = articlePath(article.slug);
  const url = absoluteUrl(path);
  const title = articleTitle(article);

  return {
    title,
    lang: LOCALE_TAGS[DEFAULT_LOCALE],
    tags: [
      ...baseTags({
        path,
        locale: DEFAULT_LOCALE,
        title,
        description: article.description,
        ogType: "article",
        robots: article.noindex ? HIDDEN : INDEXABLE,
        translated: false,
      }),
      { tag: "meta", attrs: { property: "article:published_time", content: article.date } },
      { tag: "meta", attrs: { property: "article:modified_time", content: article.updated } },
      { tag: "meta", attrs: { property: "article:section", content: article.category } },
      ...article.tags.map(
        (tag): HeadTag => ({ tag: "meta", attrs: { property: "article:tag", content: tag } }),
      ),
      { tag: "jsonLd", data: buildBreadcrumbJsonLd(articleCrumbs(article)) },
      {
        tag: "jsonLd",
        data: buildArticleJsonLd({
          article,
          url,
          readingTimeMinutes: readingTimeMinutes(article),
        }),
      },
    ],
  };
}

export type StaticRouteKind = "home" | "articleIndex" | "article" | "faq";

export interface StaticRoute {
  path: string;
  kind: StaticRouteKind;
  locale: Locale;
  head: PageHead;
  /** Only an indexable route reaches the sitemap and `llms.txt`. */
  indexable: boolean;
  /** The label a discovery artifact lists this route under. */
  label: string;
  /** The other languages of this same page, for the sitemap's alternates. */
  alternates: { locale: Locale; path: string }[];
  /** `lastmod`, where the page has an honest one. */
  lastModified?: string;
  article?: Article;
}

function alternatesFor(path: string): { locale: Locale; path: string }[] {
  return LOCALES.map((locale) => ({ locale, path: localePath(locale, path) }));
}

/**
 * The public URL set, derived. Everything downstream reads this: the
 * prerenderer, the sitemap, `llms.txt`, and the verifier that checks all three.
 */
export function staticRoutes(): StaticRoute[] {
  const localized = LOCALES.flatMap((locale): StaticRoute[] => {
    const t = messagesFor(locale);

    return [
      {
        path: localePath(locale, "/"),
        kind: "home",
        locale,
        head: buildHomeHead(locale),
        indexable: true,
        label: t.seo.homeLabel,
        alternates: alternatesFor("/"),
      },
      {
        path: localePath(locale, ARTICLES_PATH),
        kind: "articleIndex",
        locale,
        head: buildArticleIndexHead(locale),
        indexable: true,
        label: t.seo.articlesLabel,
        alternates: alternatesFor(ARTICLES_PATH),
      },
      {
        path: localePath(locale, FAQ_PATH),
        kind: "faq",
        locale,
        head: buildFaqHead(locale),
        indexable: true,
        label: t.seo.faqLabel,
        alternates: alternatesFor(FAQ_PATH),
      },
    ];
  });

  return [
    ...localized,
    // Every article is prerendered, including a hidden one: `noindex` decides
    // what the discovery artifacts advertise, not whether the URL exists. They
    // exist in English only, so they carry no alternates.
    ...listArticles().map(
      (article): StaticRoute => ({
        path: articlePath(article.slug),
        kind: "article",
        locale: DEFAULT_LOCALE,
        head: buildArticleHead(article),
        indexable: !article.noindex,
        label: article.title,
        alternates: [],
        lastModified: article.updated,
        article,
      }),
    ),
  ];
}

export { SITE_URL };
